import styles from "./styles.scss";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Profile } from "@/assets/icons";
import { getInitialsAvatar } from "./get-initials-avatar";
import {
  BASE_AVATAR_SIZE,
  BASE_FONT_SIZE,
  BASE_LINE_HEIGHT,
  BASE_STATUS_SIZE,
} from "./constants";
import { getIconStatus } from "./utils";
import { UserAvatarProps } from "./types";

export const UserAvatar = ({
  name = "",
  surname = "",
  source = "",
  size = BASE_AVATAR_SIZE,
  className,
  hasBorder,
  status,
  statusSize = BASE_STATUS_SIZE,
  onClick,
  onlyIcon: OnlyIcon,
}: UserAvatarProps): JSX.Element => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isError, setIsError] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleAvatarLoaded = () => {
    setIsImageLoaded(true);
  };

  const handleAvatarError = () => {
    setIsError(true);
  };

  const renderIcon = () => {
    const Icon = getIconStatus(status);

    return Icon ? (
      <Icon width={statusSize} height={statusSize} />
    ) : (
      <div
        className={styles.unknownStatus}
        style={{ width: `${statusSize}px`, height: `${statusSize}px` }}
      />
    );
  };

  useEffect(() => {
    setIsImageLoaded(false);
    setIsError(false);
  }, [source]);

  const { initials, avatarColor } = getInitialsAvatar(name, surname);

  return (
    <div
      onClick={onClick}
      ref={wrapperRef}
      style={{
        width: `${size}px`,
        height: `${size}px`,
      }}
      className={clsx(
        styles.avatarContainer,
        isImageLoaded && !isError && styles.avatarContainerLoaded,
        hasBorder && styles.avatarContainerWithBorder,
        onClick && styles.avatarContainerClickable,
        className,
      )}
    >
      <div
        style={{
          fontSize: `${(size / BASE_AVATAR_SIZE) * BASE_FONT_SIZE}px`,
          lineHeight: `${(size / BASE_AVATAR_SIZE) * BASE_LINE_HEIGHT}px`,
        }}
        className={clsx(styles.namePlaceholder, avatarColor)}
      >
        {OnlyIcon ? <OnlyIcon /> : initials || <Profile />}
      </div>

      {!OnlyIcon && (
        <img
          className={styles.avatar}
          src={source}
          alt="User avatar"
          role="presentation"
          onLoad={handleAvatarLoaded}
          onError={handleAvatarError}
        />
      )}

      {status && <div className={styles.status}>{renderIcon()}</div>}
    </div>
  );
};
